import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ShieldCheck, Plane, Lock, Download } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEticket() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [u, setU] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/verify/eticket/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "E-ticket tidak ditemukan"));
  }, [id]);

  const loginAndOpen = async () => {
    setLoading(true);
    try {
      const { data: t } = await axios.post(`${API}/api/auth/login`, u);
      localStorage.setItem("gg_token", t.token);
      window.open(`${API}/api/etickets/${id}/original.pdf?token=${t.token}`, "_blank");
      toast.success("Admin verified — PDF OTA asli terbuka");
    } catch (e) {
      toast.error("Login gagal");
    } finally { setLoading(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#f6f8f7] p-6">
    <Card className="max-w-md w-full"><CardContent className="pt-8 text-center space-y-3">
      <div className="text-3xl">❌</div><h1 className="text-xl font-extrabold text-red-700">Verifikasi Gagal</h1>
      <p className="text-slate-600">{err}</p></CardContent></Card></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Memverifikasi...</div>;

  return (
    <div className="min-h-screen bg-[#f6f8f7] py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#e4ecc9]"><ShieldCheck size={28} className="text-[#556b2f]"/></div>
          <h1 className="text-3xl font-extrabold text-[#556b2f] tracking-tight">E-Ticket Verified</h1>
          <p className="text-sm text-slate-500">Issued by <b>{data.issuer}</b></p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center gap-2 text-[#556b2f]"><CheckCircle2 size={18}/> <span className="font-semibold">Authentic Gogojalan E-Ticket</span></div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">E-Ticket #</div><div className="mono font-bold">{data.eticket_number}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Airline Ref / PNR</div><div className="mono font-bold">{data.airline_ref || "-"}</div></div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Traveller(s)</div>
              {data.passengers.map((p, i) => <div key={i} className="font-semibold">{p.name}</div>)}
            </div>
            <div className="space-y-3">
              {data.flights.map((f, i) => (
                <div key={i} className="p-3 rounded-lg border border-slate-200 bg-[#fafcf3]">
                  <div className="flex items-center gap-2"><Plane size={16} className="text-[#556b2f]"/><span className="font-bold text-[#556b2f]">{f.flight_number}</span><span className="text-slate-700">· {f.carrier}</span></div>
                  <div className="mt-1 text-sm text-slate-700">{f.from_airport} → {f.to_airport}</div>
                  <div className="text-xs text-slate-500 mono">{f.depart} → {f.arrival}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2 font-semibold text-slate-800"><Lock size={16}/> Admin Access: Original OTA PDF</div>
            <p className="text-xs text-slate-500">Login admin untuk membuka PDF asli dari OTA (Agoda / Traveloka / dll).</p>
            {!showLogin ? (
              <Button onClick={() => setShowLogin(true)} className="w-full bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="verify-login-btn"><Lock size={14} className="mr-1"/> Login sebagai Admin</Button>
            ) : (
              <div className="space-y-2">
                <div><Label>Email</Label><Input type="email" value={u.email} onChange={(e) => setU({ ...u, email: e.target.value })} data-testid="verify-email" placeholder="admin@gogojalan.id"/></div>
                <div><Label>Password</Label><Input type="password" value={u.password} onChange={(e) => setU({ ...u, password: e.target.value })} data-testid="verify-password"/></div>
                <Button disabled={loading} onClick={loginAndOpen} className="w-full bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="verify-open"><Download size={14} className="mr-1"/> {loading ? "..." : "Login & Buka PDF Asli"}</Button>
              </div>
            )}
            <Button variant="ghost" onClick={() => nav("/")} className="w-full text-xs">Kembali ke Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
